import { SearchResultsSortKey } from '@internal/blocks/SearchResultsBlock/SearchResultsSortKey'
import { Locale, LocaleEnum } from '@internal/utils/machine/i18n/Locale'
import { TLocale } from '@internal/utils/machine/i18n/TLocale'
import { SystemPageKey } from 'page/SystemPageKey'
import { TRoute } from './TRoute'
import { TRouteParams } from './TRouteParams'
import { TRedirect } from './redirects/TRedirect'
import { getRedirectForMachine } from './redirects/getRedirectForMachine'
import { getRedirectForMachineComparePage } from './redirects/getRedirectForMachineComparePage'
import { getRedirectForSearchResultsPage } from './redirects/getRedirectForSearchResultsPage'
import { redirect } from './redirects/redirect'
import { searchResultsPageRoute } from './searchResultsPageRoute'
import { systemPageRoute } from './systemPages/systemPageRoute'

export type TRoutingTableEntry = Record<TLocale, TRoute<any>>

export type TSearchResultsPageRouteParams = TRouteParams<
  {
    machineTypeSlug?: string
    brandSlug?: string
    modelSlug?: string
    brand?: string
    model?: string
  },
  {
    headerType?: string
    baleChamber?: string
    machineSubType?: string
    brand?: string[]
    model?: string[]
    baseModel?: string[]
    modelSeries?: string[]
    amount?: string
    location?: string[]
    feature?: string[]
    sort?: SearchResultsSortKey
  }
>

export type TMachineDetailsRouteParams = TRouteParams<
  {
    machineType: string
    brand: string
    model: string
    machineGroupId: string
  },
  {}
>
export type TCompareMachinesRouteParams = TRouteParams<
  {
    machineGroupId0?: string
    machineGroupId1?: string
    machineGroupId2?: string
  },
  {}
>

export type TDashboardRouteParams = TRouteParams<{}, {}>

const genericCompareRoutePrefix = 'compare'
const compareRoutePrefix: Record<TLocale, string> = {
  [Locale.en]: genericCompareRoutePrefix,
  [Locale.de]: 'vergleichen',
  [Locale.fr]: 'comparer',
  [Locale.el]: 'singrisi',
  [Locale.it]: 'confronta',
  [Locale.pl]: 'porownaj',
  [Locale.ro]: 'comparare',
  [Locale.ru]: 'sravnit',
  [Locale.hu]: 'osszehasonlitas',
  [Locale.es]: 'comparar',
  [Locale.bg]: 'sravnjavane',
  [Locale.sl]: 'primerjaj',
  [Locale.sr]: 'uporedite',
  [Locale.da]: 'ammenlign',
  [Locale.cs]: 'porovnat',
  [Locale.lt]: 'palyginti',
}

export const usedFarmMachineryRoutePrefix: Record<TLocale, string> = {
  [Locale.de]: 'gebrauchte-landmaschinen',
  [Locale.en]: 'used-farm-machinery',
  [Locale.fr]: 'machines-agricoles-doccasion',
  [Locale.el]: 'metaxeirismena-georgika-mixanimata',
  [Locale.it]: 'macchine-agricole-usate',
  [Locale.pl]: 'uzywane-maszyny-rolnicze',
  [Locale.ro]: 'utilaje-agricole-second',
  [Locale.ru]: 'bu-technika',
  [Locale.hu]: 'hasznalt-mezogazdasagi-gepek',
  [Locale.es]: 'maquinaria-agricola-usada',
  [Locale.bg]: 'izpolzvani-selskostopanski-mashini',
  [Locale.sl]: 'rabljena-kmetijska-mehanizacija',
  [Locale.sr]: 'polovne-poljoprivredne-masine',
  [Locale.da]: 'brugte-landbrugsmaskiner',
  [Locale.cs]: 'pouzite-zemedelske-stroje',
  [Locale.lt]: 'naudota-zemes-ukio-technika',
}

export const searchRoute: Record<TLocale, string> = {
  [Locale.de]: 'suche',
  [Locale.en]: 'search',
  [Locale.fr]: 'recherche',
  [Locale.el]: 'anazitisi',
  [Locale.it]: 'ricerca',
  [Locale.pl]: 'wyszukiwanie',
  [Locale.ro]: 'cautare',
  [Locale.ru]: 'poisk',
  [Locale.hu]: 'kereses',
  [Locale.es]: 'busqueda',
  [Locale.bg]: 'tarsene',
  [Locale.sl]: 'iskanje',
  [Locale.sr]: 'pretraga',
  [Locale.da]: 'soegning',
  [Locale.cs]: 'hledani',
  [Locale.lt]: 'paieska',
}

const shortPdpRoute = '/:locale/:machineGroupId([a-zA-Z0-9]{7})/'

const locales = Object.values(LocaleEnum)
export const routingTable: {
  systemPageRoutes: Record<SystemPageKey, TRoutingTableEntry>
  searchResultsPage: TRedirect[]
  shortPdp: TRedirect
  redirects: TRedirect[]
  machineRoutes: {
    nonStrictPdp: TRoutingTableEntry
    short: string
  }
} = {
  systemPageRoutes: {
    [SystemPageKey.StartPage]: locales.reduce<TRoutingTableEntry>(
      (map, locale) => ({
        ...map,
        [locale]: systemPageRoute(SystemPageKey.StartPage, `/${locale}/`),
      }),
      {} as TRoutingTableEntry
    ),

    // this needs to be accessible, so we can get a preview of the cms content
    // and don't have to wait for another deployment / server side generation of the 404 page
    // hitting this url will return a HTTP 200 (not a 404)
    [SystemPageKey.NotFound]: locales.reduce<TRoutingTableEntry>(
      (map, locale) => ({
        ...map,
        [locale]: systemPageRoute(SystemPageKey.NotFound, `/${locale}/_404/`),
      }),
      {} as TRoutingTableEntry
    ),

    // this needs to be accessible, so we can get a preview of the cms content
    // and don't have to wait for another deployment / server side generation of the 500 page
    // hitting this url will return a HTTP 200 (not a 500)
    [SystemPageKey.InternalServerError]: locales.reduce<TRoutingTableEntry>(
      (map, locale) => ({
        ...map,
        [locale]: systemPageRoute(
          SystemPageKey.InternalServerError,
          `/${locale}/_500/`
        ),
      }),
      {} as TRoutingTableEntry
    ),

    [SystemPageKey.PDP]: locales.reduce<TRoutingTableEntry>(
      (map, locale) => ({
        ...map,
        [locale]: systemPageRoute<TMachineDetailsRouteParams>(
          SystemPageKey.PDP,
          `/${locale}/${usedFarmMachineryRoutePrefix[locale]}/:machineType/:brand/:model/:machineGroupId([a-z0-9]{7})/`
        ),
      }),
      {} as TRoutingTableEntry
    ),

    [SystemPageKey.CompareMachines]: locales.reduce<TRoutingTableEntry>(
      (map, locale) => ({
        ...map,
        [locale]: systemPageRoute<TCompareMachinesRouteParams>(
          SystemPageKey.CompareMachines,
          `/${locale}/${compareRoutePrefix[locale]}/:machineGroupId0([a-z0-9]{7})?/:machineGroupId1([a-z0-9]{7})?/:machineGroupId2([a-z0-9]{7})?/`
        ),
      }),
      {} as TRoutingTableEntry
    ),

    [SystemPageKey.SearchResults]: locales.reduce<TRoutingTableEntry>(
      (map, locale) => ({
        ...map,
        [locale]: searchResultsPageRoute(
          `/${locale}/${usedFarmMachineryRoutePrefix[locale]}/:machineTypeSlug/:brandSlug?/:modelSlug?/`
        ),
      }),
      {} as TRoutingTableEntry
    ),

    [SystemPageKey.Dashboard]: locales.reduce<TRoutingTableEntry>(
      (map, locale) => ({
        ...map,
        [locale]: systemPageRoute<TDashboardRouteParams>(
          SystemPageKey.Dashboard,
          `/${locale}/dashboard/`
        ),
      }),
      {} as TRoutingTableEntry
    ),
  },

  machineRoutes: {
    nonStrictPdp: locales.reduce<TRoutingTableEntry>(
      (map, locale) => ({
        ...map,
        [locale]: systemPageRoute<TMachineDetailsRouteParams>(
          SystemPageKey.PDP,
          `/${locale}/${usedFarmMachineryRoutePrefix[locale]}/:machineType/:brand/:model/:machineGroupId([a-zA-Z0-9]{7})/`
        ),
      }),
      {} as TRoutingTableEntry
    ),
    short: shortPdpRoute,
  },

  searchResultsPage: locales.map((locale) =>
    redirect(
      `/${locale}/${usedFarmMachineryRoutePrefix[locale]}/`,
      getRedirectForSearchResultsPage
    )
  ),

  shortPdp: redirect<{ machineGroupId: string }>(
    shortPdpRoute,
    getRedirectForMachine
  ),

  redirects: [
    // redirect non pure lowercase machineGroupId
    ...locales.map((locale) =>
      redirect<{ machineGroupId: string }>(
        `/${locale}/${usedFarmMachineryRoutePrefix[locale]}/:machineType/:brand/:model/:machineGroupId([a-zA-Z0-9]{7})/`,
        getRedirectForMachine
      )
    ),

    // machine compare page
    ...locales.map((locale) =>
      redirect<{
        machineGroupId0?: string
        machineGroupId1?: string
        machineGroupId2?: string
      }>(
        `/${locale}/${genericCompareRoutePrefix}/:machineGroupId0([a-zA-Z0-9]{7})?/:machineGroupId1([a-zA-Z0-9]{7})?/:machineGroupId2([a-zA-Z0-9]{7})?/`,
        getRedirectForMachineComparePage
      )
    ),
  ],
}

export const matchSearchRoute = (locale: TLocale, url: string) => {
  const result = routingTable.systemPageRoutes[SystemPageKey.SearchResults][
    locale
  ].exec(url, {})

  if (!result) {
    return null
  }

  return result.pathParams as TSearchResultsPageRouteParams['pathParams']
}
